import React from 'react'
import { Query } from 'react-apollo'
import {
  CompliancePolicy,
  VulnerabilitySeverityBreakdown
} from '../applications-grid/shared'
import { AgentLanguage } from '../agent-language'
import { AttackStatus } from '../attack-status'
import { Role } from '@contrast-security-inc/base-types'
import { AggregationOptions } from '../aggregations'
import gql from 'graphql-tag'
import { vulnerabilitySeverityBreakdownAllFragment } from '../applications-grid/fragments'

export const architectureAssociatedAppFragment = gql`
  fragment ArchitectureAssociatedApplication on Application {
    __typename
    id
    name
    language
    vulnerabilityBreakdown {
      ...VulnerabilitySeverityBreakdownAll
    }
    attackStatus
    hasAssess
    hasAProtectedServer
    role
    url
    lastActivity
    online
    primary
    moduleCount
    violatedCompliancePolicies {
      id
      name
    }
  }

  ${vulnerabilitySeverityBreakdownAllFragment}
`

const architectureAssociatedApplicationsQuery = gql`
  query ArchitectureAssociatedApplications(
    $organizationID: ID!
    $applicationID: ID!
    $componentHashes: [String]
    $tags: [String]
    $technologies: [String]
    $languages: [String]
    $servers: [String]
    $environments: [String]
    $customMetadata: [CustomMetadataFilter]
  ) {
    architectureAssociatedApplications(
      organizationID: $organizationID
      applicationID: $applicationID
      filters: {
        componentHashes: $componentHashes
        tags: $tags
        technologies: $technologies
        languages: $languages
        servers: $servers
        environments: $environments
        customMetadata: $customMetadata
      }
    ) {
      componentsHashes
      application {
        ...ArchitectureAssociatedApplication
      }
    }
  }

  ${architectureAssociatedAppFragment}
`

export interface Application {
  readonly id: string
  readonly name: string
  readonly language: AgentLanguage
  readonly vulnerabilityBreakdown: VulnerabilitySeverityBreakdown
  readonly attackStatus: AttackStatus
  readonly hasAssess: boolean
  readonly hasAProtectedServer: boolean
  readonly role: Role
  readonly url: string
  readonly lastActivity: string
  readonly online: boolean
  readonly primary: boolean
  readonly moduleCount: number
  readonly violatedCompliancePolicies: CompliancePolicy[]
}

export interface ArchitectureAssociatedApplication {
  readonly application: Application
  readonly componentHashes: string[]
}

interface ChildProps {
  readonly architectureAssociatedApplications: ArchitectureAssociatedApplication[]
  readonly loading: boolean
}

interface Props<TAggregationCategory extends string> {
  readonly applicationID: string
  readonly organizationID: string
  readonly componentHashes: string[]
  readonly aggregationFilters: AggregationOptions<TAggregationCategory>
  readonly additionalFilters?: { readonly [key: string]: any }
  readonly children: (props: ChildProps) => React.ReactNode
}

interface VarsWithoutAgg {
  readonly organizationID: string
  readonly applicationID: string
}

type Vars = VarsWithoutAgg

// TODO flowmap - add tests CONTRAST-37856
export function ArchitectureAssociatedApplicationsLoader<
  TAggregationCategory extends string
>({
  applicationID,
  organizationID,
  componentHashes,
  aggregationFilters,
  additionalFilters,
  children
}: Props<TAggregationCategory>) {
  const variables: Vars = Object.assign({}, aggregationFilters, {
    organizationID,
    applicationID,
    componentHashes,
    ...additionalFilters
  })

  return (
    <Query
      fetchPolicy="network-only"
      query={architectureAssociatedApplicationsQuery}
      variables={variables}
      errorPolicy="all"
    >
      {(result) => {
        const { data, error, loading } = result
        if (error) {
          throw new Error(error.message)
        }

        const architectureAssociatedApps =
          data.architectureAssociatedApplications || []

        return children({
          architectureAssociatedApplications:
            normalizeArchitectureAssociatedApplications(
              architectureAssociatedApps
            ),
          loading
        })
      }}
    </Query>
  )
}

const normalizeArchitectureAssociatedApplications = (
  applications
): ArchitectureAssociatedApplication[] => {
  return applications.map((app) => ({
    componentHashes: app.componentsHashes,
    application: {
      ...app.application,
      language: AgentLanguage.fromKey(app.application.language),
      attackStatus: app.application.attackStatus as AttackStatus,
      role: app.application.role as Role
    }
  }))
}
